import { useState, useRef, useCallback, useEffect } from 'react';
import {
  analyzeStreamForBlankVideo,
  captureSurfaceWarning,
} from '../utils/capturePreview';

interface ScreenCaptureOptions {
  width?: number;
  height?: number;
  frameRate?: number;
  quality?: number;
}

export interface RecordedVideo {
  id: string;
  blob: Blob;
  duration: number;
  timestamp: number;
}

interface ScreenCaptureHook {
  isRecording: boolean;
  isSupported: boolean;
  error: string | null;
  captureWarning: string | null;
  previewStream: MediaStream | null;
  recordedVideos: RecordedVideo[];
  currentRecordingDuration: number;
  startRecording: (options?: ScreenCaptureOptions) => Promise<void>;
  stopRecording: () => void;
  getMediaStream: () => MediaStream | null;
  getRecordedBlob: () => Blob | null;
  getAllRecordedVideos: () => RecordedVideo[];
  clearRecordedVideos: () => void;
}

export const useScreenCapture = (): ScreenCaptureHook => {
  const [isRecording, setIsRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captureWarning, setCaptureWarning] = useState<string | null>(null);
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const [isSupported, setIsSupported] = useState(false);
  const [recordedVideos, setRecordedVideos] = useState<RecordedVideo[]>([]);
  const [currentRecordingDuration, setCurrentRecordingDuration] = useState(0);
  
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const recordedChunksRef = useRef<Blob[]>([]);
  const recordedBlobRef = useRef<Blob | null>(null);
  const recordingStartTimeRef = useRef<number>(0);
  const durationIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Check if screen capture is supported
  const checkSupport = useCallback(() => {
    const hasMediaDevices = !!navigator.mediaDevices;
    const hasGetDisplayMedia = 'getDisplayMedia' in navigator.mediaDevices;
    const hasMediaRecorder = !!window.MediaRecorder;
    
    console.log('Screen capture support check:', {
      hasMediaDevices,
      hasGetDisplayMedia,
      hasMediaRecorder,
      userAgent: navigator.userAgent
    });
    
    const isSupported = hasMediaDevices && hasGetDisplayMedia && hasMediaRecorder;
    setIsSupported(isSupported);
    
    if (!isSupported) {
      setError('Screen capture is not supported in this browser. Please use Chrome, Firefox, or Edge.');
    }
    
    return isSupported;
  }, []);

  // Initialize support check
  useEffect(() => {
    checkSupport();
  }, [checkSupport]);

  const startRecording = useCallback(async (options: ScreenCaptureOptions = {}) => {
    try {
      console.log('Starting screen recording with options:', options);
      setError(null);

      if (!checkSupport()) {
        throw new Error('Screen capture is not supported in this browser');
      }

      console.log('Requesting screen capture...');
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          width: { ideal: options.width || 1920 },
          height: { ideal: options.height || 1080 },
          frameRate: { ideal: options.frameRate || 30 },
          cursor: 'always',
        },
        audio: true,
        preferCurrentTab: false,
        selfBrowserSurface: 'exclude',
        systemAudio: 'include',
        monitorTypeSurfaces: 'include',
      } as DisplayMediaStreamOptions);

      console.log('Screen capture stream obtained:', stream);
      mediaStreamRef.current = stream;
      setPreviewStream(stream);

      const { mostlyBlack, displaySurface } = await analyzeStreamForBlankVideo(stream);
      const warnings: string[] = [];
      const surfaceWarning = captureSurfaceWarning(displaySurface);
      if (surfaceWarning) warnings.push(surfaceWarning);
      if (mostlyBlack) {
        warnings.push(
          'The shared area looks black or empty. For SmartPSS: disable hardware acceleration, use the 32-bit client, or share the entire monitor instead of the app window.'
        );
      }
      setCaptureWarning(warnings.length > 0 ? warnings.join(' ') : null);

      // Set up MediaRecorder
      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : MediaRecorder.isTypeSupported('video/webm')
        ? 'video/webm'
        : 'video/mp4';

      console.log('Using MIME type:', mimeType);

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        videoBitsPerSecond: options.quality ? options.quality * 1000000 : 2500000 // Default 2.5 Mbps
      });

      console.log('MediaRecorder created:', mediaRecorder);
      mediaRecorderRef.current = mediaRecorder;
      recordedChunksRef.current = [];

      // Handle data available
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      // Handle recording stop
      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, { type: mimeType });
        recordedBlobRef.current = blob;
        
        // Calculate recording duration
        const duration = Math.floor((Date.now() - recordingStartTimeRef.current) / 1000);
        
        // Store this recording in the videos array
        if (blob.size > 0) {
          const recordedVideo: RecordedVideo = {
            id: `recording-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            blob,
            duration,
            timestamp: Date.now()
          };
          setRecordedVideos(prev => [...prev, recordedVideo]);
        }
        
        setIsRecording(false);
        setCurrentRecordingDuration(0);
        
        // Clear duration interval
        if (durationIntervalRef.current) {
          clearInterval(durationIntervalRef.current);
          durationIntervalRef.current = null;
        }
      };

      // Handle errors
      mediaRecorder.onerror = (event) => {
        console.error('MediaRecorder error:', event);
        setError('Recording error occurred');
        setIsRecording(false);
      };

      // Start recording
      console.log('Starting MediaRecorder...');
      recordingStartTimeRef.current = Date.now();
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      
      // Start duration timer
      setCurrentRecordingDuration(0);
      durationIntervalRef.current = setInterval(() => {
        setCurrentRecordingDuration(prev => prev + 1);
      }, 1000);
      
      console.log('Recording started successfully');

      // Handle stream end (user stops sharing)
      stream.getVideoTracks()[0].onended = () => {
        if (mediaRecorderRef.current && isRecording) {
          mediaRecorderRef.current.stop();
        }
        if (mediaStreamRef.current) {
          mediaStreamRef.current.getTracks().forEach(track => track.stop());
          mediaStreamRef.current = null;
        }
        setPreviewStream(null);
        setCaptureWarning(null);
        setIsRecording(false);
        if (durationIntervalRef.current) {
          clearInterval(durationIntervalRef.current);
          durationIntervalRef.current = null;
        }
      };

    } catch (err) {
      console.error('Screen capture error:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to start screen capture';
      console.error('Error details:', {
        error: err,
        message: errorMessage,
        stack: err instanceof Error ? err.stack : undefined
      });
      setError(errorMessage);
      setIsRecording(false);
    }
  }, [checkSupport, isRecording]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }

    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    setPreviewStream(null);
    setCaptureWarning(null);

    setIsRecording(false);
    
    // Clear duration interval
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, [isRecording]);

  const getMediaStream = useCallback(() => {
    return mediaStreamRef.current;
  }, []);

  const getRecordedBlob = useCallback(() => {
    return recordedBlobRef.current;
  }, []);

  const getAllRecordedVideos = useCallback(() => {
    return recordedVideos;
  }, [recordedVideos]);

  const clearRecordedVideos = useCallback(() => {
    setRecordedVideos([]);
    recordedBlobRef.current = null;
  }, []);

  return {
    isRecording,
    isSupported,
    error,
    captureWarning,
    previewStream,
    recordedVideos,
    currentRecordingDuration,
    startRecording,
    stopRecording,
    getMediaStream,
    getRecordedBlob,
    getAllRecordedVideos,
    clearRecordedVideos
  };
};

